import COUNTRY_DATA from "../Data/countries.json";

const EXCLUDED_COUNTRY_CODES = new Set(["AC", "EU", "IC", "TA", "XA", "XC", "XK", "XO"]);

const normalizeCountryKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const COUNTRY_LIST = COUNTRY_DATA.filter((country) => !EXCLUDED_COUNTRY_CODES.has(country.code));

const COUNTRY_BY_CODE = new Map(
  COUNTRY_LIST.map((country) => [country.code, country.label])
);
const COUNTRY_LABEL_TO_CODE = new Map(
  COUNTRY_LIST.map((country) => [normalizeCountryKey(country.label), country.code])
);

const COUNTRY_ALIAS_TO_CODE = {
  uae: "AE",
  "united arab emirates": "AE",
  turkey: "TR",
  turkiye: "TR",
  "republic of turkey": "TR",
  "republic of turkiye": "TR",
  "czech republic": "CZ",
  "ivory coast": "CI",
  "cote d'ivoire": "CI",
  "cote d ivoire": "CI",
  "cote divoire": "CI",
  "democratic republic of congo": "CD",
  "democratic republic of the congo": "CD",
  "dr congo": "CD",
  drc: "CD",
  "congo kinshasa": "CD",
  "republic of congo": "CG",
  "republic of the congo": "CG",
  "congo brazzaville": "CG",
  uk: "GB",
  gb: "GB",
  britain: "GB",
  usa: "US",
  us: "US",
  america: "US",
  "united states of america": "US",
  korea: "KR",
  "south korea": "KR",
  "north korea": "KP",
  swaziland: "SZ",
  burma: "MM",
  vietnam: "VN",
  palestine: "PS",
  "palestinian territories": "PS",
  "palestinian territory occupied": "PS",
  "palestinian territory": "PS",
};

const COUNTRY_ALIAS_LOOKUP = new Map(
  Object.entries(COUNTRY_ALIAS_TO_CODE).map(([alias, code]) => [normalizeCountryKey(alias), code])
);

const COUNTRY_ALIAS_SEARCH_TERMS = new Map();
for (const [alias, code] of Object.entries(COUNTRY_ALIAS_TO_CODE)) {
  const list = COUNTRY_ALIAS_SEARCH_TERMS.get(code) || [];
  list.push(alias);
  COUNTRY_ALIAS_SEARCH_TERMS.set(code, list);
}

export const getLocalizedCountryOptions = (locale = "en") => {
  const intlLocale = locale === "ar" || locale === "tr" ? locale : "en";
  let displayNames = null;

  try {
    if (typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function") {
      displayNames = new Intl.DisplayNames([intlLocale], { type: "region" });
    }
  } catch {
    displayNames = null;
  }

  return COUNTRY_LIST.map((country) => {
    let label = country.label;
    if (displayNames) {
      try {
        const localized = displayNames.of(country.code);
        if (localized) label = localized;
      } catch {
        // Keep the English label.
      }
    }

    const aliases = COUNTRY_ALIAS_SEARCH_TERMS.get(country.code) || [];
    return {
      value: country.code,
      label,
      searchLabel: `${label} ${country.label} ${country.code} ${aliases.join(" ")}`.trim(),
    };
  }).sort((a, b) => a.label.localeCompare(b.label, intlLocale, { sensitivity: "base" }));
};

export const COUNTRY_OPTIONS = getLocalizedCountryOptions("en");

export const COUNTRY_OPTION_LABELS = new Set(COUNTRY_OPTIONS.map((option) => option.label));
export const COUNTRY_OPTION_CODES = new Set(COUNTRY_OPTIONS.map((option) => option.value));

export const normalizeCountryInput = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const normalizedKey = normalizeCountryKey(raw);

  const aliasCode = COUNTRY_ALIAS_LOOKUP.get(normalizedKey);
  if (aliasCode) {
    return COUNTRY_BY_CODE.get(aliasCode) || "";
  }

  const upper = raw.toUpperCase();
  if (COUNTRY_OPTION_CODES.has(upper)) {
    return COUNTRY_BY_CODE.get(upper) || "";
  }

  const labelCode = COUNTRY_LABEL_TO_CODE.get(normalizedKey);
  if (labelCode) {
    return COUNTRY_BY_CODE.get(labelCode) || "";
  }

  return "";
};

/** Resolve free-text / ISO input to an ISO-3166 alpha-2 code when known. */
export const resolveCountryCodeFromInput = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const upper = raw.toUpperCase();
  if (COUNTRY_OPTION_CODES.has(upper)) return upper;
  const normalizedKey = normalizeCountryKey(raw);
  const aliasCode = COUNTRY_ALIAS_LOOKUP.get(normalizedKey);
  if (aliasCode) return aliasCode;
  return COUNTRY_LABEL_TO_CODE.get(normalizedKey) || "";
};

export const isValidCountryInput = (value) => Boolean(normalizeCountryInput(value));
